import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, Grid, TextField, MenuItem, FormControl } from '@mui/material';
import { useLanguage } from '../../../../context/LanguageContext';
import { useStoreData } from '../../../../context/StoreDataContext';
import { checkPostalCode } from '../../../../services/postalService';

export interface AddressFormData {
    address1: string;
    address2: string;
    address3_type: string;
    address3_num: string;
    address3_name: string;
    address4: string;
    zip_code: string;
    local_city_name: string;
    short_city_name: string;
    local_country_name: string;
}

interface AddressFormProps {
    data: AddressFormData;
    onChange: (field: keyof AddressFormData, value: string) => void;
}

const AddressForm = ({ data, onChange }: AddressFormProps) => {
    const { t } = useLanguage();
    const { wayTypes, countries, loading } = useStoreData();
    const [zipCode, setZipCode] = useState(data.zip_code);
    const [city, setCity] = useState(data.local_city_name);
    const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
    const [postalLoading, setPostalLoading] = useState(false);
    const zipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    
    // Refs pour les inputs textuels
    const address3NumRef = useRef<HTMLInputElement>(null);
    const address3NameRef = useRef<HTMLInputElement>(null);
    const address1Ref = useRef<HTMLInputElement>(null);
    const address2Ref = useRef<HTMLInputElement>(null);
    const shortCityNameRef = useRef<HTMLInputElement>(null);
    const address4Ref = useRef<HTMLInputElement>(null);
    
    const handleBlur = useCallback((field: keyof AddressFormData, ref: React.RefObject<HTMLInputElement | null>) => {
        onChange(field, ref.current?.value || '');
    }, [onChange]);

    useEffect(() => () => {
        if (zipTimer.current) {
            clearTimeout(zipTimer.current);
        }
    }, []);

    const handleZipChange = useCallback((value: string) => {
        setZipCode(value);
        onChange('zip_code', value);

        if (zipTimer.current) {
            clearTimeout(zipTimer.current);
        }

        if (!/^\d{5}$/.test(value)) {
            setCitySuggestions([]);
            setPostalLoading(false);
            return;
        }

        zipTimer.current = setTimeout(async () => {
            setPostalLoading(true);
            const cities = await checkPostalCode(value);
            setCitySuggestions(cities);
            setPostalLoading(false);
        }, 400);
    }, [onChange]);

    const handleCityChange = useCallback((value: string) => {
        setCity(value);
        onChange('local_city_name', value);
    }, [onChange]);

    return (
        <Grid container spacing={2}>
            {/* Numéro */}
            <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                    inputRef={address3NumRef}
                    size="small"
                    fullWidth
                    label={t('pages.store.addressForm.number')}
                    defaultValue={data.address3_num}
                    onBlur={() => handleBlur('address3_num', address3NumRef)}
                    inputProps={{ maxLength: 8 }}
                />
            </Grid>

            {/* Type de voie */}
            <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                    <TextField
                        size="small"
                        select
                        fullWidth
                        label={t('pages.store.addressForm.wayType')}
                        value={data.address3_type}
                        onChange={(e) => onChange('address3_type', e.target.value)}
                        disabled={loading}
                        SelectProps={{
                            MenuProps: {
                                sx: { height: "300px" },
                            },
                        }}
                    >
                        {loading && (
                            <MenuItem disabled>{t('common.loading')}...</MenuItem>
                        )}
                        {!loading && wayTypes && wayTypes.length === 0 && (
                            <MenuItem disabled>{t('common.noData')}</MenuItem>
                        )}
                        {!loading && wayTypes && wayTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                                {type.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </FormControl>
            </Grid>

            {/* Nom de la voie */}
            <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                    inputRef={address3NameRef}
                    size="small"
                    fullWidth
                    label={t('pages.store.addressForm.wayName')}
                    defaultValue={data.address3_name}
                    onBlur={() => handleBlur('address3_name', address3NameRef)}
                    inputProps={{ maxLength: 35 }}
                />
            </Grid>

            {/* Code postal */}
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    size="small"
                    fullWidth
                    label={t('pages.store.zipCode')}
                    value={zipCode}
                    onChange={(e) => handleZipChange(e.target.value)}
                    inputProps={{ maxLength: 5 }}
                    InputProps={{ endAdornment: postalLoading ? <CircularProgress size={14} /> : undefined }}
                />
            </Grid>

            {/* Ville */}
            <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                    freeSolo
                    options={citySuggestions}
                    value={city}
                    inputValue={city}
                    onChange={(_, value) => handleCityChange(value || '')}
                    onInputChange={(_, value) => handleCityChange(value)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            fullWidth
                            label={t('pages.store.city')}
                            inputProps={{ ...params.inputProps, maxLength: 50 }}
                        />
                    )}
                />
            </Grid>

            {/* Appartement, étage */}
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    inputRef={address1Ref}
                    size="small"
                    fullWidth
                    label={t('pages.store.addressForm.apartment')}
                    defaultValue={data.address1}
                    onBlur={() => handleBlur('address1', address1Ref)}
                    inputProps={{ maxLength: 38 }}
                />
            </Grid>

            {/* Entrée, bâtiment */}
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    inputRef={address2Ref}
                    size="small"
                    fullWidth
                    label={t('pages.store.addressForm.building')}
                    defaultValue={data.address2}
                    onBlur={() => handleBlur('address2', address2Ref)}
                    inputProps={{ maxLength: 38 }}
                />
            </Grid>

            {/* Référence ville */}
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    inputRef={shortCityNameRef}
                    size="small"
                    fullWidth
                    label={t('pages.store.addressForm.cityRef')}
                    defaultValue={data.short_city_name}
                    onBlur={() => handleBlur('short_city_name', shortCityNameRef)}
                    inputProps={{ maxLength: 38 }}
                />
            </Grid>

            {/* Lieu dit */}
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    inputRef={address4Ref}
                    size="small"
                    fullWidth
                    label={t('pages.store.addressForm.locality')}
                    defaultValue={data.address4}
                    onBlur={() => handleBlur('address4', address4Ref)}
                    inputProps={{ maxLength: 38 }}
                />
            </Grid>

            {/* Pays */}
            <Grid size={{ xs: 12, md: 12 }}>
                <FormControl fullWidth>
                    <TextField
                        size="small"
                        select
                        required
                        fullWidth
                        label={t('pages.store.addressForm.country')}
                        value={data.local_country_name}
                        onChange={(e) => onChange('local_country_name', e.target.value)}
                        disabled={loading}
                        SelectProps={{
                            MenuProps: {
                                sx: { height: "300px" },
                            },
                        }}
                    >
                        {loading && (
                            <MenuItem disabled>{t('common.loading')}...</MenuItem>
                        )}
                        {!loading && countries && countries.length === 0 && (
                            <MenuItem disabled>{t('common.noData')}</MenuItem>
                        )}
                        {!loading && countries && countries.map((country) => (
                            <MenuItem key={country.id} value={country.name}>
                                {country.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </FormControl>
            </Grid>
        </Grid>
    );
};

export default AddressForm;
